import { authenticatedFetch } from '../utils/api';

export interface PendingFileSync {
  isbn: string;
  username: string;
  folder: string;
  queuedAt: string;
}

export interface FileSyncState {
  isbn: string;
  username: string;
  folder: string;
  status: 'syncing' | 'failed';
  progress: {
    filesTotal: number;
    filesSynced: number;
    filesDeleted: number;
    currentOperation: string;
  };
  error?: string;
  startedAt: string;
  syncedTo?: ('prod' | 'nonprod')[];
}

export interface CompletedFileSync {
  isbn: string;
  username: string;
  folder: string;
  status: 'completed' | 'failed';
  filesSynced: number;
  filesDeleted: number;
  filesTotal: number;
  error?: string;
  startedAt: string;
  completedAt: string;
  syncedTo?: ('prod' | 'nonprod')[];
}

export interface SyncStatusResponse {
  success: boolean;
  pending: PendingFileSync[];
  active: FileSyncState[];
  completed: CompletedFileSync[];
  failed: CompletedFileSync[];
  totalSuccessfullyProcessed: number;
}

class ProductionFileSyncService {
  private lastKnownServerCount: number | null = null; // Server count at last login/refresh (null = not initialized)

  /**
   * Initialize count tracking on login or refresh
   * Should be called after successful login or on page refresh to capture the server's current count
   */
  async initializeCountTracking(): Promise<void> {
    try {
      const response = await authenticatedFetch('/api/books/production-file-sync/production-file-sync-status');
      if (!response.ok) {
        throw new Error(`Failed to fetch sync status: ${response.statusText}`);
      }
      const data = await response.json();
      this.lastKnownServerCount = data.totalSuccessfullyProcessed || 0;
      console.log('[ProductionFileSyncService] Initialized count tracking:', this.lastKnownServerCount);
    } catch (error) {
      console.error('[ProductionFileSyncService] Failed to initialize count tracking:', error);
      this.lastKnownServerCount = 0;
    }
  }

  /**
   * Get current sync status (pending queue + active syncs)
   * Returns adjusted count (subtracts count from login/refresh time)
   * If count tracking not initialized, initializes it first
   */
  async getSyncStatus(): Promise<SyncStatusResponse> {
    try {
      // If count tracking not initialized, initialize it now
      if (this.lastKnownServerCount === null) {
        await this.initializeCountTracking();
      }

      const response = await authenticatedFetch('/api/books/production-file-sync/production-file-sync-status');
      if (!response.ok) {
        throw new Error(`Failed to fetch sync status: ${response.statusText}`);
      }
      const data = await response.json();
      
      // Adjust count: subtract what was already processed at login/refresh time
      // If server count is less than last known, server was restarted - reset to 0
      const serverCount = data.totalSuccessfullyProcessed || 0;
      if (this.lastKnownServerCount !== null && serverCount < this.lastKnownServerCount) {
        // Server was restarted
        this.lastKnownServerCount = 0;
      }
      const adjustedCount = this.lastKnownServerCount !== null 
        ? Math.max(0, serverCount - this.lastKnownServerCount)
        : 0;
      
      return {
        ...data,
        totalSuccessfullyProcessed: adjustedCount,
      };
    } catch (error) {
      console.error('[ProductionFileSyncService] Error fetching sync status:', error);
      throw error;
    }
  }

  /**
   * Add ISBNs to the sync queue (batch). Use this for both single and bulk; pass [isbn] for one book.
   * Returns which were accepted and which were rejected (invalid format or over limit).
   */
  async addToQueueBatch(
    isbns: string[],
    folder: string
  ): Promise<{ success: boolean; accepted: string[]; rejected: Array<{ isbn: string; reason: string }>; error?: string }> {
    try {
      const response = await authenticatedFetch(
        `/api/books/production-file-sync-batch?folder=${encodeURIComponent(folder)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isbns }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        return { success: false, accepted: [], rejected: [], error: data.error || 'Request failed' };
      }
      return {
        success: true,
        accepted: data.accepted || [],
        rejected: data.rejected || [],
      };
    } catch (error: any) {
      console.error('[ProductionFileSyncService] Error in addToQueueBatch:', error);
      return {
        success: false,
        accepted: [],
        rejected: [],
        error: error.message || 'Sync request failed',
      };
    }
  }
}

export const productionFileSyncService = new ProductionFileSyncService();

